import React, { useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, limit, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Container, Card, Alert, Row, Col, Button, Badge } from 'react-bootstrap';

// Default profile image as data URL
const defaultProfileImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='24' height='24'%3E%3Cpath fill='%23CCC' d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z'/%3E%3C/svg%3E";

const ViewProfile = () => {
  const { userId } = useParams();
  const [user, loading, authError] = useAuthState(auth);
  const [profile, setProfile] = useState(null);
  const [recentAds, setRecentAds] = useState([]);
  const [totalAdsCount, setTotalAdsCount] = useState(0);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        console.log("User not authenticated");
        setFetchLoading(false);
        return;
      }

      if (!userId) {
        console.error("No userId provided to ViewProfile component");
        setError('No user ID provided');
        setFetchLoading(false);
        return;
      }

      try {
        console.log("Authenticated user:", user.uid);
        console.log("Attempting to fetch user document for userId:", userId);
        
        const userDoc = await getDoc(doc(db, 'users', userId));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log("User data fetched:", userData);
          setProfile({
            id: userId,
            displayName: userData.displayName || 'User',
            email: userData.email,
            phoneNumber: userData.phoneNumber || 'Not provided',
            profileImage: userData.profileImage || defaultProfileImage,
            bio: userData.bio || 'No bio provided',
            joinDate: userData.joinDate
          });

          // Get recent listings by this user
          const adsRef = collection(db, 'ads');
          const adsQuery = query(
            adsRef,
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            limit(3)
          );

          const adsSnapshot = await getDocs(adsQuery);
          const adsList = adsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setRecentAds(adsList);
          setTotalAdsCount(adsSnapshot.size);
        } else {
          console.error("User document not found for userId:", userId);
          setError('User not found');
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError(`Error fetching user profile: ${err.message}`);
      } finally {
        setFetchLoading(false);
      }
    };

    if (!loading && !authError) {
      fetchProfile();
    }
  }, [userId, user, loading, authError]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp.toDate()).toLocaleDateString();
  };

  if (loading || fetchLoading) return <Container><Alert variant="info">Loading...</Alert></Container>;
  if (authError) return <Container><Alert variant="danger">Authentication error: {authError.message}</Alert></Container>;
  if (!user) return <Navigate to="/login" />;
  if (error) return <Container><Alert variant="danger">{error}</Alert></Container>;
  if (!profile) return <Container><Alert variant="warning">User not found</Alert></Container>;

  return (
    <Container className="mt-4">
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3} className="text-center">
              <img 
                src={profile.profileImage} 
                alt={profile.displayName}
                className="img-fluid rounded-circle mb-3"
                style={{ maxWidth: '150px', height: '150px', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = defaultProfileImage;
                }}
              />
            </Col>
            <Col md={9}>
              <h2>{profile.displayName}</h2>
              <p><strong>Joined:</strong> {formatDate(profile.joinDate)}</p>
              <p><strong>Contact:</strong> {profile.phoneNumber}</p>
              <p>{profile.bio}</p>
              <Button 
                variant="primary" 
                as={Link} 
                to={`/messages/new/${profile.id}`}
                className="mt-2"
              >
                Send Message
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <h3>Recent Listings <Badge bg="secondary">{totalAdsCount} total</Badge></h3>
      {recentAds.length > 0 ? (
        <Row>
          {recentAds.map(ad => (
            <Col md={4} key={ad.id} className="mb-3">
              <Card>
                <Card.Img 
                  variant="top" 
                  src={ad.imageUrl || ad.imageUrls?.[0] || 'https://via.placeholder.com/150'} 
                  className="card-img-top"
                />
                <Card.Body>
                  <Card.Title>{ad.ItemName}</Card.Title>
                  <Card.Text>
                    R{ad.Price ? ad.Price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") : 'Not specified'}
                  </Card.Text>
                  <Button 
                    variant="outline-primary" 
                    as={Link} 
                    to={`/view-ad/${ad.id}`}
                    size="sm"
                  >
                    View Details
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Alert variant="info">No listings found</Alert>
      )}
    </Container>
  );
};

export default ViewProfile;